import { getArrowImage, getIcon } from './Icons';
import { useBlendColors } from 'hooks/useDesign';
import { alpha } from '@mui/material';
import { Theme } from '@mui/material/styles/createTheme';

export const getLineageDiagramConfig = (themeObj: Theme) => {
    const styles = {
        nodes: {
            // color: {
            //     background: themeObj.palette.background.default,
            //     border: themeObj.palette.primary.main,
            //     highlight: {
            //         border: themeObj.palette.primary.main,
            //         background: useBlendColors(alpha(themeObj.palette.primary.main, 0.15)),
            //     },
            //     hover: {
            //         border: themeObj.palette.primary.main,
            //         background: useBlendColors(alpha(themeObj.palette.primary.main, 0.15)),
            //     },
            // },
            // icon: themeObj.palette.primary.main,
            font: {
                background: themeObj.palette.background.default,
                color: themeObj.palette.text.primary,
            },
        },
        edges: {
            color: useBlendColors(alpha(themeObj.palette.text.primary, 0.4)),
        },
    };

    return {
        layout: {
            improvedLayout: true,
            hierarchical: {
                direction: 'LR',
                sortMethod: 'directed',
            },
        },
        nodes: {
            image: {
                unselected: getIcon(themeObj, 'element'),
                selected: getIcon(themeObj, 'element', undefined, undefined, false, false, false, 'clicked'),
            },
            shape: 'image',
            font: styles.nodes.font,
        },
        edges: {
            arrows: {
                to: {
                    enabled: true,
                    type: 'image',
                    imageHeight: 11,
                    imageWidth: 9,
                    src: getArrowImage(styles.edges.color),
                },
            },
            chosen: false,
            color: styles.edges.color,
            arrowStrikethrough: false,
        },
        physics: {
            enabled: true,
            solver: 'hierarchicalRepulsion',
            hierarchicalRepulsion: {
                nodeDistance: 175,
                damping: 0.2,
                springLength: 75,
            },
            timestep: 0.8,
        },
        interaction: {
            dragNodes: false,
            dragView: true,
            selectConnectedEdges: false,
            zoomView: true,
            navigationButtons: true,
            keyboard: true,
            hover: true,
        },
        // Groups whose nodes always receive a per-node image in Lineage.tsx (tables, views, semantic
        // models, BI reports, data sources) must not define one here: vis re-applies the group image
        // on every setOptions() call, including the one that releases nodes from an opened cluster,
        // and that would wipe the plugin / engine badge those nodes carry.
        groups: {
            report: {
                image: {
                    unselected: getIcon(themeObj, 'report'),
                    selected: getIcon(
                        themeObj,
                        'report',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            metric: {
                image: {
                    unselected: getIcon(themeObj, 'metric'),
                    selected: getIcon(
                        themeObj,
                        'metric',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'external-metric': {
                image: {
                    unselected: getIcon(themeObj, 'external-metric'),
                    selected: getIcon(
                        themeObj,
                        'external-metric',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'manual-metric': {
                image: {
                    unselected: getIcon(themeObj, 'metric'),
                    selected: getIcon(
                        themeObj,
                        'metric',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'external-report': {
                image: {
                    unselected: getIcon(themeObj, 'external-report'),
                    selected: getIcon(
                        themeObj,
                        'external-report',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'external-content': {
                image: {
                    unselected: getIcon(themeObj, 'external-content'),
                    selected: getIcon(
                        themeObj,
                        'external-content',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            burst: {
                image: {
                    unselected: getIcon(themeObj, 'burst'),
                    selected: getIcon(
                        themeObj,
                        'burst',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'dataset-view': {
                image: {
                    unselected: getIcon(themeObj, 'dataset-view'),
                    selected: getIcon(
                        themeObj,
                        'dataset-view',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            dataset: {
                image: {
                    unselected: getIcon(themeObj, 'dataset'),
                    selected: getIcon(
                        themeObj,
                        'dataset',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            plugin: {
                image: {
                    unselected: getIcon(themeObj, 'plugin'),
                    selected: getIcon(
                        themeObj,
                        'plugin',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'deleted-dataset': {
                image: {
                    unselected: getIcon(themeObj, 'deleted-dataset'),
                    selected: getIcon(
                        themeObj,
                        'deleted-dataset',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            element: {
                image: {
                    unselected: getIcon(themeObj, 'element'),
                    selected: getIcon(
                        themeObj,
                        'element',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'multi-metric': {
                image: {
                    unselected: getIcon(themeObj, 'multi-metric'),
                    selected: getIcon(
                        themeObj,
                        'multi-metric',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
            'legacy-report': {
                image: {
                    unselected: getIcon(themeObj, 'legacy-report'),
                    selected: getIcon(
                        themeObj,
                        'legacy-report',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        'clicked',
                    ),
                },
            },
        },
    };
};
